import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import styles from './TopNav.module.css';

interface NavItem {
  id: string;
  configKey: string | null;
  icon: string;
  text: string;
  isLink: boolean;
  path?: string;
}

const baseNavItems: NavItem[] = [
  { id: 'home', configKey: 'nav_home_enabled', icon: 'home', text: 'HOME', isLink: true, path: '/' },
  { id: 'plugins', configKey: 'nav_plugins_enabled', icon: 'https://i.postimg.cc/gJFCV1Rz/Sem-T-tulo-1.png', text: 'LAUNCHER', isLink: true, path: '/plugins' },
  { id: 'shop', configKey: 'nav_shop_enabled', icon: 'apparel', text: 'SHOP', isLink: true, path: '/shop' },
  { id: 'kits', configKey: 'nav_kits_enabled', icon: 'audio_file', text: 'KITS', isLink: true, path: '/kits' },
  { id: 'about', configKey: 'nav_about_enabled', icon: 'psychology_alt', text: 'ABOUT', isLink: true, path: '/about' },
  { id: 'cart', configKey: null, icon: 'shopping_cart', text: 'CART', isLink: false },
  { id: 'account', configKey: null, icon: 'account_circle', text: 'Account', isLink: false },
];

interface TopNavProps {
  onCartClick: () => void;
  onAccountClick: () => void;
  loggedInUserRole: 'guest' | 'user' | 'admin';
}

const TopNav: React.FC<TopNavProps> = ({ onCartClick, onAccountClick, loggedInUserRole }) => {
  const location = useLocation();
  const indicatorRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [navConfig, setNavConfig] = useState<Record<string, string>>({});

  // Fetch nav visibility flags from backend
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/config`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setNavConfig(data))
      .catch(() => {
        // Fallback: show all items if API fails
        setNavConfig({});
      });
  }, []);

  const navItems = React.useMemo(() => {
    // Filter items based on config flags
    let items = baseNavItems.filter(item => {
      if (!item.configKey) return true; // cart/account always visible
      // If config was fetched and the flag is explicitly 'false', hide the item
      if (navConfig[item.configKey] === 'false') return false;
      return true; // Default: show item (including when config hasn't loaded yet)
    });

    if (loggedInUserRole === 'admin') {
      const adminItem: NavItem = {
        id: 'admin-dashboard',
        configKey: null,
        icon: 'admin_panel_settings',
        text: 'ADMIN',
        isLink: true,
        path: '/admin'
      };
      items.splice(items.length - 2, 0, adminItem);
    }
    return items;
  }, [loggedInUserRole, navConfig]);

  const activeIndex = navItems.findIndex(item => {
    if (!item.isLink) return false;
    if (item.path === '/') return location.pathname === '/';
    if (item.path) return location.pathname.startsWith(item.path);
    return false;
  });

  useEffect(() => {
    const activeItem = itemRefs.current[activeIndex];
    if (activeItem && indicatorRef.current) {
      const timer = setTimeout(() => {
        if (indicatorRef.current) {
          indicatorRef.current.style.transition = 'left 0.6s cubic-bezier(0.3, -0.01, 0.06, 1), width 0.6s cubic-bezier(0.3, -0.01, 0.06, 1), opacity 0.3s ease';
          indicatorRef.current.style.left = `${activeItem.offsetLeft}px`;
          indicatorRef.current.style.width = `${activeItem.offsetWidth}px`;
          indicatorRef.current.style.opacity = '1';
        }
      }, 50);
      return () => clearTimeout(timer);
    } else if (indicatorRef.current) {
      indicatorRef.current.style.opacity = '0';
    }
  }, [activeIndex, location.pathname, navItems]);

  const renderContent = (item: { icon: string, text: string, id: string }) => (
    <>
      <span className={styles.icon}>
        {item.icon.startsWith('http') ? (
          <img style={{ width: '30px', height: '30px', objectFit: 'contain' }} src={item.icon} alt={item.text} />
        ) : (
          <span className="material-symbols-outlined notranslate" translate="no">{item.icon}</span>
        )}
      </span>
      <span className={styles.text}>{item.text}</span>
    </>
  );

  return (
    <nav className={styles.topMainNav}>
      <ul>
        {navItems.map((item, index) => (
          <li
            key={item.id}
            ref={(el) => { itemRefs.current[index] = el; }}
            className={`${styles.list} ${activeIndex === index ? styles.active : ''}`}
          >
            {item.isLink ? (
              <Link to={item.path!}>{renderContent(item)}</Link>
            ) : (
              <button
                className={styles.actionButton}
                onClick={() => {
                  if (item.id === 'cart') {
                    onCartClick();
                  } else {
                    onAccountClick();
                  }
                }}
              >
                {renderContent(item)}
              </button>
            )}
          </li>
        ))}
        <div ref={indicatorRef} className={styles.indicator}></div>
      </ul>
    </nav>
  );
};

export default TopNav;